import React, { useState } from "react";
import Header from "../Component/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { supabase } from "../supabase/client";
import { searchAvailableBuses } from "../services/busAvailability";
import { userErrorMessage } from "../utils/userErrorMessage";

function getTodayString() {
  return new Date().toISOString().slice(0, 10);
}

function createSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `web-test-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getInitialChatSessionId() {
  const existing = localStorage.getItem("busChatTestSessionId");
  if (existing) return existing;
  const next = createSessionId();
  localStorage.setItem("busChatTestSessionId", next);
  return next;
}

const initialChatMessages = [
  { role: "AI", text: "버스 예약 내용을 말씀해주세요." },
];

export default function BusAvailabilityTest() {
  const [date, setDate] = useState(getTodayString());
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("18:00");
  const [passengers, setPassengers] = useState("28");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [aiMessage, setAiMessage] = useState("8월 20일 오전 8시에 서울에서 홍천으로 28명이 갈 거고 저녁 8시까지 쓸 거야");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState("");
  const [chatSessionId, setChatSessionId] = useState(getInitialChatSessionId);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState(initialChatMessages);
  const [chatState, setChatState] = useState(null);
  const [chatRawResult, setChatRawResult] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState("");

  const companyId = localStorage.getItem("email") || "";

  const handleSearch = async () => {
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const searchResult = await searchAvailableBuses({
        date,
        startTime,
        endTime,
        passengers: Number(passengers),
        companyId,
      });
      if (process.env.NODE_ENV !== "production") console.log("Bus availability test result:", searchResult);
      setResult(searchResult);
    } catch (e) {
      console.error("Bus availability test failed:", e);
      setError(userErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleAiParse = async () => {
    setAiLoading(true);
    setAiError("");
    setAiResult(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke("parse-bus-request", {
        body: { message: aiMessage },
      });
      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);
      if (process.env.NODE_ENV !== "production") console.log("AI bus request parse result:", data);
      setAiResult(data?.result || data);
    } catch (e) {
      console.error("AI bus request parse failed:", e);
      setAiError(userErrorMessage(e));
    } finally {
      setAiLoading(false);
    }
  };

  const canSearchWithAi = Boolean(
    aiResult?.date &&
    aiResult?.startTime &&
    aiResult?.passengers &&
    companyId
  );

  const handleAiSearch = async () => {
    if (!canSearchWithAi) return;

    setAiSearchLoading(true);
    setError("");
    setResult(null);

    try {
      const searchResult = await searchAvailableBuses({
        date: aiResult.date,
        startTime: aiResult.startTime,
        endTime: aiResult.endTime,
        passengers: aiResult.passengers,
        companyId,
      });
      if (process.env.NODE_ENV !== "production") console.log("AI condition bus availability result:", searchResult);
      setResult(searchResult);
    } catch (e) {
      console.error("AI condition bus availability failed:", e);
      setError(userErrorMessage(e));
    } finally {
      setAiSearchLoading(false);
    }
  };

  const handleChatSend = async () => {
    const message = chatInput.trim();
    if (!message) return;

    setChatLoading(true);
    setChatError("");
    setChatInput("");
    setChatMessages((prev) => [...prev, { role: "사용자", text: message }]);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke("bus-chat", {
        body: { sessionId: chatSessionId, message },
      });
      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);

      if (process.env.NODE_ENV !== "production") console.log("Bus chat result:", data);
      setChatRawResult(data);
      setChatState(data.state || null);

      if (data.status === "SEARCH_COMPLETE") {
        setChatMessages((prev) => [...prev, { role: "AI", text: "이용 가능한 차량을 확인했습니다." }]);
        setResult({
          available: data.availableBuses || [],
          availableGroups: data.availableGroups || [],
          unavailable: data.unavailableBuses || [],
        });
      } else if (data.replyText) {
        setChatMessages((prev) => [...prev, { role: "AI", text: data.replyText }]);
      } else if (data.followUpQuestion) {
        setChatMessages((prev) => [...prev, { role: "AI", text: data.followUpQuestion }]);
      } else {
        setChatMessages((prev) => [...prev, { role: "AI", text: "예약 조건을 계속 말씀해주세요." }]);
      }
    } catch (e) {
      console.error("Bus chat failed:", e);
      setChatError(userErrorMessage(e));
      setChatMessages((prev) => [...prev, { role: "AI", text: "대화 처리 중 오류가 발생했습니다." }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatReset = () => {
    const nextSessionId = createSessionId();
    localStorage.setItem("busChatTestSessionId", nextSessionId);
    setChatSessionId(nextSessionId);
    setChatInput("");
    setChatMessages(initialChatMessages);
    setChatState(null);
    setChatRawResult(null);
    setChatError("");
  };

  return (
    <div>
      <Header />
      <main style={{ padding: "16px 24px 32px" }}>
        <section style={{ marginBottom: 16 }}>
          <h1 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 700 }}>
            차량 가용 조회 테스트
          </h1>
          <p style={{ margin: 0, color: "#b45309", fontWeight: 600 }}>
            개발용 임시 테스트 화면입니다. Supabase SELECT 조회만 수행하고 DB를 변경하지 않습니다.
          </p>
        </section>

        <section
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
            alignItems: "end",
            marginBottom: 16,
          }}
        >
          <label style={{ display: "grid", gap: 6 }}>
            <span>날짜</span>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 6 }}>
            <span>출발시간</span>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 6 }}>
            <span>종료시간</span>
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 6 }}>
            <span>탑승인원</span>
            <Input
              type="number"
              min="1"
              value={passengers}
              onChange={(e) => setPassengers(e.target.value)}
            />
          </label>
          <Button onClick={handleSearch} disabled={loading || !companyId}>
            {loading ? "검색 중..." : "차량 검색 테스트"}
          </Button>
        </section>

        <section style={{ marginBottom: 16, color: "#475569" }}>
          현재 companyId: <strong>{companyId || "로그인 회사 정보 없음"}</strong>
        </section>

        <section style={{ margin: "24px 0", padding: 16, background: "#f8fafc", borderRadius: 6 }}>
          <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700 }}>
            AI 자연어 분석 테스트
          </h2>
          <p style={{ margin: "0 0 12px", color: "#b45309", fontWeight: 600 }}>
            개발용 임시 테스트 영역입니다. 고객 발화를 DB에 저장하지 않고 Edge Function에서만 OpenAI를 호출합니다.
          </p>
          <textarea
            value={aiMessage}
            onChange={(e) => setAiMessage(e.target.value)}
            rows={4}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: 10,
              border: "1px solid #cbd5e1",
              borderRadius: 6,
              resize: "vertical",
              marginBottom: 10,
            }}
          />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <Button onClick={handleAiParse} disabled={aiLoading || !aiMessage.trim()}>
              {aiLoading ? "분석 중..." : "AI 분석"}
            </Button>
            <Button onClick={handleAiSearch} disabled={!canSearchWithAi || aiSearchLoading}>
              {aiSearchLoading ? "검색 중..." : "AI 조건으로 차량 검색"}
            </Button>
          </div>

          {aiError && (
            <div style={{ marginBottom: 12, color: "#b91c1c", fontWeight: 600 }}>
              AI 분석 실패: {aiError}
            </div>
          )}

          {aiResult && (
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                background: "#111827",
                color: "#e5e7eb",
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
                margin: 0,
              }}
            >
              {JSON.stringify(aiResult, null, 2)}
            </pre>
          )}
        </section>

        <section style={{ margin: "24px 0", padding: 16, background: "#f1f5f9", borderRadius: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 10 }}>
            <div>
              <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700 }}>
                대화형 AI 테스트
              </h2>
              <p style={{ margin: 0, color: "#b45309", fontWeight: 600 }}>
                개발용 임시 채팅입니다. 원문 대화는 저장하지 않고 예약 검색 state만 ai_chat_sessions에 저장합니다.
              </p>
            </div>
            <Button onClick={handleChatReset} disabled={chatLoading}>
              대화 초기화
            </Button>
          </div>

          <div style={{ marginBottom: 10, color: "#475569", fontSize: 13 }}>
            sessionId: <strong>{chatSessionId}</strong>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(280px, 0.6fr)", gap: 16 }}>
            <div>
              <div
                style={{
                  minHeight: 220,
                  maxHeight: 360,
                  overflowY: "auto",
                  background: "#ffffff",
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 10,
                }}
              >
                {chatMessages.map((item, index) => (
                  <div
                    key={`${item.role}-${index}`}
                    style={{
                      display: "flex",
                      justifyContent: item.role === "사용자" ? "flex-end" : "flex-start",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "78%",
                        padding: "8px 10px",
                        borderRadius: 6,
                        background: item.role === "사용자" ? "#dbeafe" : "#e5e7eb",
                        color: "#111827",
                      }}
                    >
                      <strong>{item.role}: </strong>{item.text}
                    </div>
                  </div>
                ))}
              </div>

              {chatError && (
                <div style={{ marginBottom: 10, color: "#b91c1c", fontWeight: 600 }}>
                  대화 처리 실패: {chatError}
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.nativeEvent.isComposing) handleChatSend();
                  }}
                  placeholder="예: 8월 20일 서울에서 홍천으로 28명 갈 거야"
                />
                <Button onClick={handleChatSend} disabled={chatLoading || !chatInput.trim()}>
                  {chatLoading ? "전송 중..." : "보내기"}
                </Button>
              </div>
            </div>

            <div>
              <h3 style={{ margin: "0 0 8px", fontSize: 16 }}>CURRENT STATE</h3>
              <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "110px 1fr", gap: "6px 8px" }}>
                {["date", "start", "destination", "passengers", "startTime", "endTime", "returnDate"].map((field) => (
                  <React.Fragment key={field}>
                    <dt style={{ color: "#475569" }}>{field}</dt>
                    <dd style={{ margin: 0, fontWeight: 600 }}>{chatState?.[field] ?? "-"}</dd>
                  </React.Fragment>
                ))}
              </dl>

              {chatRawResult && (
                <>
                  <h3 style={{ margin: "16px 0 8px", fontSize: 16 }}>RAW CHAT RESULT</h3>
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      background: "#111827",
                      color: "#e5e7eb",
                      padding: 12,
                      borderRadius: 6,
                      fontSize: 12,
                      maxHeight: 260,
                      overflowY: "auto",
                    }}
                  >
                    {JSON.stringify(chatRawResult, null, 2)}
                  </pre>
                </>
              )}
            </div>
          </div>
        </section>

        {error && (
          <section style={{ marginBottom: 16, color: "#b91c1c", fontWeight: 600 }}>
            조회 실패: {error}
          </section>
        )}

        {result && (
          <>
            <section style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>AVAILABLE GROUPS</h2>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["조합", "대수", "총 좌석", "남는 좌석", "차량"].map((label) => (
                        <th key={label} style={tableHeaderStyle}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(result.availableGroups || []).length === 0 ? (
                      <tr><td colSpan="5" style={tableCellStyle}>검색된 가능 차량 조합이 없습니다.</td></tr>
                    ) : result.availableGroups.map((group, index) => (
                      <tr key={`group-${index}`}>
                        <td style={tableCellStyle}>{index + 1}</td>
                        <td style={tableCellStyle}>{group.busCount}</td>
                        <td style={tableCellStyle}>{group.totalSeats}</td>
                        <td style={tableCellStyle}>{group.remainingSeats}</td>
                        <td style={{ ...tableCellStyle, textAlign: "left" }}>
                          {(group.buses || []).map((bus) => (
                            <div key={`${index}-${bus.carNumber}-${bus.fullCarNumber}`}>
                              {bus.carNumber} / {bus.carName} / {bus.seatType} / {bus.seatCount}인승 / {bus.year}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>AVAILABLE</h2>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["차량번호", "차량명", "차량종류", "좌석수", "연식"].map((label) => (
                        <th key={label} style={tableHeaderStyle}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.available.length === 0 ? (
                      <tr><td colSpan="5" style={tableCellStyle}>검색된 가능 차량이 없습니다.</td></tr>
                    ) : result.available.map((bus) => (
                      <tr key={`${bus.carNumber}-${bus.fullCarNumber}`}>
                        <td style={tableCellStyle}>{bus.carNumber}</td>
                        <td style={tableCellStyle}>{bus.carName}</td>
                        <td style={tableCellStyle}>{bus.seatType}</td>
                        <td style={tableCellStyle}>{bus.seatCount}</td>
                        <td style={tableCellStyle}>{bus.year}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>UNAVAILABLE</h2>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["차량번호", "제외 이유", "충돌한 예약시간"].map((label) => (
                        <th key={label} style={tableHeaderStyle}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.unavailable.length === 0 ? (
                      <tr><td colSpan="3" style={tableCellStyle}>시간 충돌 차량이 없습니다.</td></tr>
                    ) : result.unavailable.map((bus) => {
                      const conflict = bus.conflictingReservation || {};
                      return (
                        <tr key={`${bus.carNumber}-${conflict.date}-${conflict.time}`}>
                          <td style={tableCellStyle}>{bus.carNumber}</td>
                          <td style={tableCellStyle}>{bus.reason}</td>
                          <td style={tableCellStyle}>
                            {conflict.date} {conflict.time || "-"} ~ {conflict.arrivalTime || "출발 후 1시간 기준"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>원본 결과 JSON</h2>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  background: "#0f172a",
                  color: "#e2e8f0",
                  padding: 16,
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                {JSON.stringify(result, null, 2)}
              </pre>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

const tableHeaderStyle = {
  borderBottom: "1px solid #cbd5e1",
  padding: "8px 10px",
  textAlign: "center",
  background: "#f8fafc",
};

const tableCellStyle = {
  borderBottom: "1px solid #e2e8f0",
  padding: "8px 10px",
  textAlign: "center",
};
